import { z } from 'zod';
import type { UploadMaterialInfo } from '../MaterialUploadSheet/MaterialUploader';
import type { BatchUploadFolderFile, BatchUploadFolderNode } from '@/api/modules/folder/types';

type UploadStatus = 'pending' | 'uploading' | 'success' | 'error';

/** 依据浏览器 Entry 标志收窄为文件接口 */
const isFileEntry = (entry: FileSystemEntry): entry is FileSystemFileEntry => entry.isFile;

/** 依据浏览器 Entry 标志收窄为目录接口，并排除空拖拽项 */
const isDirectoryEntry = (
  entry: FileSystemEntry | null | undefined
): entry is FileSystemDirectoryEntry => Boolean(entry?.isDirectory);

export interface CollectedFolderFile {
  file: File;
  relativePath: string;
}

export interface CollectedFolderEntries {
  files: CollectedFolderFile[];
  folderPaths: string[];
}

export interface FolderUploadFileItem {
  uid: string;
  file: File;
  relativePath: string;
  folderPath: string;
  progress: number;
  status: UploadStatus;
  info?: UploadMaterialInfo;
  errorMessage?: string;
  fileKey?: string;
  /** 前端预计算的文件 MD5，上传完成时优先合并进 info.md5 */
  computedMd5?: string;
  /** MD5 是否仍在计算中（用于「计算中」展示与父级禁用提交） */
  md5Computing?: boolean;
  /** 文件名。刷新后 `file` 句柄会被持久化层剥离，展示与重试判定都要靠它 */
  fileName?: string;
  /** 是否可重传（任务明细页在目录树里渲染单条重试按钮时透传） */
  retryable?: boolean;
}

export interface FolderUploadTreeNode {
  name: string;
  path: string;
  files: FolderUploadFileItem[];
  children: FolderUploadTreeNode[];
  totalCount: number;
  successCount: number;
  uploadingCount: number;
  calculatingCount: number;
  errorCount: number;
}

export interface SuccessfulMaterialFolderGroup {
  folderPath: string;
  infos: UploadMaterialInfo[];
}

export interface FolderDepthLimitViolation {
  folderPath: string;
  folderDepth: number;
  targetDepth: number;
  totalDepth: number;
}

/**
 * 规范化文件相对路径
 * 拖拽和选择文件夹返回的路径分隔符可能不一致，这里统一成前端目录树使用的 `/`
 */
export const normalizeRelativePath = (path: string) => {
  return path.replace(/\\/g, '/').replace(/^\/+|\/+$/g, '');
};

/**
 * 获取文件所在目录路径
 */
export const getFolderPathFromRelativePath = (relativePath: string) => {
  const normalizedPath = normalizeRelativePath(relativePath);
  const parts = normalizedPath.split('/').filter(Boolean);
  return parts.length > 1 ? parts.slice(0, -1).join('/') : '';
};

/**
 * 计算目录路径层级
 */
export const calculateFolderPathDepth = (folderPath?: string) => {
  if (!folderPath) return 0;
  return normalizeRelativePath(folderPath).split('/').filter(Boolean).length;
};

/**
 * 获取目录路径所属的最外层父文件夹名称
 */
export const getRootFolderName = (folderPath?: string) => {
  return normalizeRelativePath(folderPath || '').split('/').filter(Boolean)[0] || '';
};

/**
 * 获取本次上传包含的最外层父文件夹列表
 * 上传文件夹只允许一次上传一个父文件夹，避免多个根目录合并到同一次导入
 */
export const getRootFolderNamesFromFolderPaths = (folderPaths: string[]) => {
  return Array.from(new Set(folderPaths.map((folderPath) => getRootFolderName(folderPath)).filter(Boolean)));
};

/**
 * 获取本次上传文件夹的最大目录层级
 */
export const getMaxFolderDepthFromFiles = (files: Pick<FolderUploadFileItem, 'folderPath'>[]) => {
  return files.reduce((maxDepth, file) => {
    return Math.max(maxDepth, calculateFolderPathDepth(file.folderPath));
  }, 0);
};

/**
 * 查找超过目录层级限制的上传目录
 */
export const findFolderDepthLimitViolations = (
  files: Pick<FolderUploadFileItem, 'folderPath'>[],
  targetDepth: number,
  maxDepth: number
) => {
  const folderPaths = Array.from(new Set(files.map((file) => normalizeRelativePath(file.folderPath))));

  return folderPaths.reduce<FolderDepthLimitViolation[]>((violations, folderPath) => {
    const folderDepth = calculateFolderPathDepth(folderPath);
    const totalDepth = targetDepth + folderDepth;
    if (totalDepth > maxDepth) {
      violations.push({
        folderPath,
        folderDepth,
        targetDepth,
        totalDepth,
      });
    }
    return violations;
  }, []);
};

/**
 * 读取文件类型的 webkit entry
 */
const readFileEntry = (entry: FileSystemFileEntry, parentPath: string) => {
  return new Promise<CollectedFolderFile>((resolve, reject) => {
    entry.file(
      (file) => {
        resolve({
          file,
          relativePath: normalizeRelativePath(`${parentPath}/${file.name}`),
        });
      },
      (error) => reject(error)
    );
  });
};

/**
 * 分批读取目录 entry 下的所有子节点
 * readEntries 可能分多批返回，必须循环读取到空数组才算结束
 */
const readDirectoryEntries = (entry: FileSystemDirectoryEntry) => {
  const reader = entry.createReader();
  const entries: FileSystemEntry[] = [];

  return new Promise<FileSystemEntry[]>((resolve, reject) => {
    const readNextBatch = () => {
      reader.readEntries(
        (batch) => {
          if (!batch.length) {
            resolve(entries);
            return;
          }
          entries.push(...batch);
          readNextBatch();
        },
        (error) => reject(error)
      );
    };

    readNextBatch();
  });
};

/**
 * 递归读取拖拽文件夹中的文件
 */
const collectFilesFromEntry = async (
  entry: FileSystemEntry,
  parentPath = ''
): Promise<CollectedFolderEntries> => {
  const currentPath = normalizeRelativePath(parentPath ? `${parentPath}/${entry.name}` : entry.name);

  if (isFileEntry(entry)) {
    return {
      files: [await readFileEntry(entry, parentPath)],
      folderPaths: [],
    };
  }

  if (!isDirectoryEntry(entry)) {
    throw new Error('无法识别拖拽目录节点');
  }
  const children = await readDirectoryEntries(entry);
  const childrenEntries = await Promise.all(
    children.map((child) => collectFilesFromEntry(child, currentPath))
  );
  return {
    files: childrenEntries.flatMap((item) => item.files),
    folderPaths: [
      currentPath,
      ...childrenEntries.flatMap((item) => item.folderPaths),
    ],
  };
};

/**
 * 从拖拽事件的 DataTransferItemList 中读取文件夹文件
 * 当前需求只使用 webkitGetAsEntry，不做 getAsFileSystemHandle 增强
 */
export const collectFilesFromDataTransferItems = async (items: DataTransferItemList) => {
  const itemList = Array.from(items);
  const entries = itemList
    .filter((item) => item.kind === 'file')
    .map((item) => item.webkitGetAsEntry?.())
    .filter(isDirectoryEntry);

  const collectedEntries = await Promise.all(entries.map((entry) => collectFilesFromEntry(entry)));
  return {
    files: collectedEntries.flatMap((item) => item.files),
    folderPaths: collectedEntries.flatMap((item) => item.folderPaths),
  };
};

/**
 * 根据素材上传状态聚合目录统计
 */
const applyFileStatusToNode = (node: FolderUploadTreeNode, file: FolderUploadFileItem) => {
  node.totalCount += 1;
  if (file.status === 'success') node.successCount += 1;
  if (file.status === 'uploading') node.uploadingCount += 1;
  if (file.status !== 'error' && file.md5Computing) node.calculatingCount += 1;
  if (file.status === 'error') node.errorCount += 1;
};

/**
 * 获取或创建目录树节点
 * 空文件夹没有文件项，也需要进入展示树和提交参数
 */
const ensureFolderUploadTreeNode = (
  folderPath: string,
  rootNodes: FolderUploadTreeNode[],
  nodeMap: Map<string, FolderUploadTreeNode>
) => {
  const folderParts = normalizeRelativePath(folderPath).split('/').filter(Boolean);
  let currentChildren = rootNodes;
  let currentNode: FolderUploadTreeNode | undefined;

  folderParts.forEach((part, index) => {
    const path = folderParts.slice(0, index + 1).join('/');
    let node = nodeMap.get(path);
    if (!node) {
      node = {
        name: part,
        path,
        files: [],
        children: [],
        totalCount: 0,
        successCount: 0,
        uploadingCount: 0,
        calculatingCount: 0,
        errorCount: 0,
      };
      nodeMap.set(path, node);
      currentChildren.push(node);
    }
    currentNode = node;
    currentChildren = node.children;
  });

  return currentNode;
};

/**
 * 构建上传目录树
 * 文件会挂在自己所在目录上，父目录同步汇总所有子目录的上传状态
 */
export const buildFolderUploadTree = (files: FolderUploadFileItem[], folderPaths: string[] = []) => {
  const rootNodes: FolderUploadTreeNode[] = [];
  const nodeMap = new Map<string, FolderUploadTreeNode>();

  folderPaths.forEach((folderPath) => {
    ensureFolderUploadTreeNode(folderPath, rootNodes, nodeMap);
  });

  files.forEach((file) => {
    const folderParts = normalizeRelativePath(file.folderPath).split('/').filter(Boolean);

    folderParts.forEach((_, index) => {
      const path = folderParts.slice(0, index + 1).join('/');
      const node = ensureFolderUploadTreeNode(path, rootNodes, nodeMap);
      if (!node) return;
      applyFileStatusToNode(node, file);
    });

    const leafNode = nodeMap.get(normalizeRelativePath(file.folderPath));
    if (leafNode) {
      leafNode.files.push(file);
    }
  });

  return rootNodes;
};

/**
 * 按目录分组成功上传的素材
 * 失败和上传中的文件不参与提交，符合“仅成功上传素材导入资源中心”的规则
 */
export const groupSuccessfulMaterialsByFolder = (files: FolderUploadFileItem[]) => {
  const groupMap = new Map<string, UploadMaterialInfo[]>();

  files.forEach((file) => {
    if (file.status !== 'success' || !file.info) return;
    const folderPath = normalizeRelativePath(file.folderPath);
    const infos = groupMap.get(folderPath) || [];
    infos.push(file.info);
    groupMap.set(folderPath, infos);
  });

  return Array.from(groupMap.entries()).map<SuccessfulMaterialFolderGroup>(([folderPath, infos]) => ({
    folderPath,
    infos,
  }));
};

/**
 * 转换素材文件元信息中的数值字段
 * 上传回调里的宽高可能是字符串，批量上传文件夹接口按数字或 null 接收
 */
const normalizeMaterialNumber = (value?: string | number | null) => {
  if (value === undefined || value === null || value === '') return null;
  const numberValue = Number(value);
  return Number.isNaN(numberValue) ? null : numberValue;
};

/**
 * 组装批量上传文件夹接口的文件参数
 */
const buildBatchUploadFolderFile = (info: UploadMaterialInfo): BatchUploadFolderFile => ({
  ossKey: info.ossKey,
  md5: info.md5,
  width: normalizeMaterialNumber(info.width),
  height: normalizeMaterialNumber(info.height),
  duration: info.duration ?? null,
});

/**
 * 获取或创建批量上传接口目录节点
 */
const ensureBatchUploadFolderNode = (
  folderPath: string,
  rootNodes: BatchUploadFolderNode[],
  nodeMap: Map<string, BatchUploadFolderNode>
) => {
  const folderParts = normalizeRelativePath(folderPath).split('/').filter(Boolean);
  let currentChildren = rootNodes;
  let currentNode: BatchUploadFolderNode | undefined;

  folderParts.forEach((folderName, index) => {
    const currentPath = folderParts.slice(0, index + 1).join('/');
    let node = nodeMap.get(currentPath);

    if (!node) {
      node = {
        folderName,
        sortOrder: 0,
        files: [],
        children: [],
      };
      nodeMap.set(currentPath, node);
      currentChildren.push(node);
    }

    currentNode = node;
    currentChildren = node.children;
  });

  return currentNode;
};

/**
 * 将成功上传的素材按本地目录路径组装成后端需要的递归 folders 树
 * 失败和上传中的文件不参与提交，避免导入未完成素材
 */
export const buildBatchUploadFolders = (files: FolderUploadFileItem[], folderPaths: string[] = []) => {
  const rootNodes: BatchUploadFolderNode[] = [];
  const nodeMap = new Map<string, BatchUploadFolderNode>();

  folderPaths.forEach((folderPath) => {
    ensureBatchUploadFolderNode(folderPath, rootNodes, nodeMap);
  });

  files.forEach((file) => {
    if (file.status !== 'success' || !file.info) return;

    const node = ensureBatchUploadFolderNode(file.folderPath, rootNodes, nodeMap);
    node?.files.push(buildBatchUploadFolderFile(file.info));
  });

  return rootNodes;
};

/** 设计师 / 创意人跨字段校验：两者均非必填，但至少填一个 */
const hasDesignerOrCreative = (data: { designerId?: string; creativeId?: string }) =>
  Boolean(data.designerId) || Boolean(data.creativeId);

const AT_LEAST_ONE_ASSIGNEE = {
  message: '设计师与创意人至少选择一个',
  path: ['designerId'],
};

/** 上传文件夹表单基础结构（不含跨字段校验），异步模式在其上收紧 folderId */
export const baseFolderUploadFormSchema = z.object({
  folderId: z.string().optional(),
  /** 设计师（非必填，与 creativeId 至少填一个） */
  designerId: z.string().optional(),
  /** 创意人（非必填，与 designerId 至少填一个） */
  creativeId: z.string().optional(),
  tagIds: z.array(z.string()).optional(),
  files: z.array(z.unknown()).optional(),
});

/** 同步模式下的表单结构 */
export const folderUploadFormSchema = baseFolderUploadFormSchema.refine(
  hasDesignerOrCreative,
  AT_LEAST_ONE_ASSIGNEE
);

/**
 * 上传文件夹抽屉的表单校验规则。
 *
 * 异步（后台任务）模式下后端 create 入参要求 `folderCategory` 必填，
 * 而它由「上传目标文件夹」反查得到 → 异步模式下目标文件夹必须选。
 * 同步模式维持原口径（允许不选目标文件夹）。
 */
export const buildFolderUploadFormSchema = (asyncMode: boolean) =>
  asyncMode
    ? baseFolderUploadFormSchema
        .extend({ folderId: z.string().min(1, '请选择上传目标文件夹') })
        .refine(hasDesignerOrCreative, AT_LEAST_ONE_ASSIGNEE)
    : folderUploadFormSchema;
